import { create } from 'zustand'
import { doc, updateDoc } from 'firebase/firestore'
import { db } from '@/config/firebase'
import { useAuthStore, type AuthState } from '@/stores/auth'
import {
  DEFAULT_HUB_PERSONALIZATION,
  HUB_CARD_IDS,
  PERSONALIZATION_VERSION,
  mergeHubPersonalization,
  needsPersonalizationPrompt
} from '@/config/hubPersonalization'

/**
 * Port of `src/stores/personalization.js`.
 *
 * `hubPersonalization` and `needsPrompt` were computeds over the auth store.
 * They are kept as plain fields here and recomputed by a subscription to the
 * auth store whenever `userProfile` or `isAuthenticated` changes.
 */

export type HubPersonalization = ReturnType<typeof mergeHubPersonalization>

export interface PersonalizationState {
  checklistOpen: boolean
  // derived from the auth store
  hubPersonalization: HubPersonalization
  needsPrompt: boolean
  // actions
  isCardVisible: (cardId: string) => boolean
  openChecklist: () => void
  closeChecklist: () => void
  saveHubPersonalization: (options?: {
    visibleCards?: Record<string, boolean>
    defaultLanding?: string
    useDefaults?: boolean
  }) => Promise<{ success: boolean, error?: string }>
}

const computeFromAuth = (authStore: AuthState) => ({
  hubPersonalization: mergeHubPersonalization(authStore.userProfile?.hubPersonalization),
  needsPrompt: (() => {
    if (!authStore.isAuthenticated || !authStore.userProfile) return false
    return needsPersonalizationPrompt(authStore.userProfile.hubPersonalization)
  })()
})

export const usePersonalizationStore = create<PersonalizationState>()((set, get) => {
  const isCardVisible = (cardId) => {
    if (!useAuthStore.getState().isAuthenticated) return true
    if (!HUB_CARD_IDS.includes(cardId)) return true
    return get().hubPersonalization.visibleCards[cardId] !== false
  }

  const openChecklist = () => {
    set({ checklistOpen: true })
  }

  const closeChecklist = () => {
    set({ checklistOpen: false })
  }

  const saveHubPersonalization = async ({
    visibleCards,
    defaultLanding,
    useDefaults = false
  }: {
    visibleCards?: Record<string, boolean>
    defaultLanding?: string
    useDefaults?: boolean
  } = {}) => {
    const authStore = useAuthStore.getState()
    const uid = authStore.user?.uid
    if (!uid) {
      return { success: false, error: 'Not signed in' }
    }

    const merged = useDefaults
      ? { ...DEFAULT_HUB_PERSONALIZATION }
      : mergeHubPersonalization({
        visibleCards,
        defaultLanding,
        completedAt: authStore.userProfile?.hubPersonalization?.completedAt,
        promptVersion: authStore.userProfile?.hubPersonalization?.promptVersion
      })

    if (!useDefaults) {
      const enabledCount = HUB_CARD_IDS.filter((id) => merged.visibleCards[id] !== false).length
      if (enabledCount === 0) {
        return { success: false, error: 'Show at least one home section' }
      }
    }

    const payload = {
      hubPersonalization: {
        visibleCards: merged.visibleCards,
        defaultLanding: merged.defaultLanding,
        completedAt: new Date().toISOString(),
        promptVersion: PERSONALIZATION_VERSION
      }
    }

    try {
      await updateDoc(doc(db, 'users', uid), payload)
      return { success: true }
    } catch (error) {
      return { success: false, error: error.message || 'Failed to save preferences' }
    }
  }

  return {
    checklistOpen: false,
    ...computeFromAuth(useAuthStore.getState()),
    isCardVisible,
    openChecklist,
    closeChecklist,
    saveHubPersonalization
  }
})

// computed(() => … authStore.userProfile / authStore.isAuthenticated …)
useAuthStore.subscribe((state, prev) => {
  if (state.userProfile !== prev.userProfile || state.isAuthenticated !== prev.isAuthenticated) {
    usePersonalizationStore.setState(computeFromAuth(state))
  }
})
